<script>
    import {FormatAndRound, NMStrength, PercStrength, PredBoundaries} from './calculations.js';
    import { _ } from 'svelte-i18n';
    import { navigate } from "svelte-routing";
    import { pick } from 'lodash';
    import moment from 'moment';
    import Button from '../Button.svelte';
    import ResultChart from './ResultChart.svelte';
    import ResultTable from './ResultTable.svelte';
    import { selectedMuscleGroups } from './stores/muscleGroupStore';
    import { formValues } from "./stores/formValueStore";
    import muscles from '../muscles.json';
    import { onMount } from "svelte";
    import { Confirm } from 'svelte-confirm';

    const navigateWithScroll = () => {
		navigate('/');
		document.querySelector('#calculate').scrollIntoView({behavior: "smooth"})
	}

    const resetForm = () => {
        selectedMuscleGroups.reset();
        formValues.reset();
        navigateWithScroll();
        //navigate('/')
    };

    const measuredValues = $selectedMuscleGroups.map(id => {
        const right = [
            $formValues[`muscle-${id}-1-right`],
            $formValues[`muscle-${id}-2-right`],
            $formValues[`muscle-${id}-3-right`],
            $formValues[`muscle-${id}-right-lever`],
        ];
        const left = [
            $formValues[`muscle-${id}-1-left`],
            $formValues[`muscle-${id}-2-left`],
            $formValues[`muscle-${id}-3-left`],
            $formValues[`muscle-${id}-left-lever`],
        ];

        return {
            group: id,
            right,
            left,
        }
    })

    const patient = pick($formValues || [], [
        'name',
        'birthdate',
        'measuredate',
        'weight',
        'gender',
        'terapeut',
    ])
    const age = moment(patient.measuredate).diff(moment(patient.birthdate), 'years', true)
    patient.age = age;

    function printResults() {
        window.print();
    }

    const nm_strength = (side, muscle) => NMStrength(side, muscle, measuredValues); // side: 1 for right, 2 for left
    const pred_boundaries = (type, muscle) => PredBoundaries(type, muscle, patient); // type: 0 for predicted, 1 for lower, 2 for upper
    const perc_strength = (side, muscle) => PercStrength(patient.age, nm_strength(side, muscle), pred_boundaries(0, muscle)) // side: 1 for right, 2 for left

    const calculatedValues = muscles.map(muscle => {
        return {
            id: muscle.id,
            name: muscle.name,
            muscleType: muscle.type,
            nmRight: nm_strength(1, muscle),
            nmLeft: nm_strength(2, muscle),
            predicted: pred_boundaries(0, muscle),
            predLower: pred_boundaries(1, muscle),
            predUpper: pred_boundaries(2, muscle),
            percRight: perc_strength(1, muscle),
            percLeft: perc_strength(2, muscle),
        }
    });

    onMount(() => {
        document.querySelector('#calculate').scrollIntoView({behavior: "smooth"})
    })
</script>

<style>
    p {
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
    }

    .results {
        max-width: 1200px;
        margin: auto
    }

    .meta {
        text-align: right;
    }

    .meta div {
        margin-bottom: var(--spacingSmall);
    }

    .meta-title {
        font-weight: 800;
    }

    .resultTable {
        width: 100%;
        overflow: auto;
    }

    .resultChart {
        width: 100%;
        margin-top: var(--spacingXXLarge);
        overflow: auto;
    }

    .button-section {
        margin-top: var(--spacingLarge);
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
    }

    .arrow-left {
        left: 10px;
        transform: rotate(180deg);
    }

    .observe {
        color: var(--colorError);
        font-weight: 800;
    }

    @media screen and (max-width: 820px) {
        .button-section {
            flex-direction: column;
            align-items: center;
        }
    }

</style>

<div class="results">
    <div class="pre-results">

    <h2>{$_('calculator.results.title')}</h2>

        <p>{$_('calculator.results.intro')}</p>

        {#if isNaN(patient.age)}
            <p><span class="observe">{$_('calculator.results.observe')}</span> {$_('calculator.results.err_age_NaN')}</p>
        {/if}
        {#if patient.age < 4.5 || patient.age > 16.5}
            <p><span class="observe">{$_('calculator.results.observe')}</span> {$_('calculator.results.err_age_range')}</p>
        {/if}
        {#if patient.weight === 0}
            <p><span class="observe">{$_('calculator.results.observe')}</span> {$_('calculator.results.err_weight_none')}</p>
        {/if}
    </div>


    <hr />

    <div class="meta">
        <div><span class="meta-title">{$_('calculator.results.name')}:</span> {patient.name || ''}</div>
        <div><span class="meta-title">{$_('calculator.results.measure_date')}:</span> {patient.measuredate || ''}</div>
        <div><span class="meta-title">{$_('calculator.results.age')}:</span> {FormatAndRound(1, patient.age)} {$_('calculator.results.years')}</div>
        <div><span class="meta-title">{$_('calculator.results.weight')}:</span> {FormatAndRound(1, patient.weight) || ''} {$_('calculator.results.kg')}</div>
        <div><span class="meta-title">{$_('calculator.results.terapeut')}:</span> {patient.terapeut || ''} </div>
    </div>

    <!-- Results are split in musclegroups for legs and arms respectively -->

    {#if $selectedMuscleGroups.some(el => el <= 10)}
        <h2>{$_('calculator.results.leg')}</h2>

        <div class="resultTable">
            <ResultTable bodypart="leg" {calculatedValues} />
        </div>
        <div class="resultChart">
            <ResultChart bodypart="leg" {calculatedValues} />
        </div>
    {/if}
    {#if $selectedMuscleGroups.some(el => el > 10)}
        <h2>{$_('calculator.results.arm')}</h2>

        <div class="resultTable">
            <ResultTable bodypart="arm" {calculatedValues} />
        </div>
        <div class="resultChart">
            <ResultChart bodypart="arm" {calculatedValues} />
        </div>
    {/if}

    <hr class="post-results" />

    <div class="button-section">
            <Button width=230px backbutton on:click={() => navigate('/calculator-1')}><span class="arrow arrow-left"></span> {$_('calculator.button_back')}</Button>
            <Confirm
                confirmTitle={$_('calculator.results.confirm_yes')}
                cancelTitle={$_('calculator.results.confirm_no')}
                let:confirm={confirmThis}
            >
                <Button width=230px backbutton on:click={() => confirmThis(resetForm)}>{$_('calculator.button_new')}</Button>
                <span slot="title">
                    {$_('calculator.results.confirm_title')}
                  </span>
                  <span slot="description">
                    {$_('calculator.results.confirm_description')}
                  </span>
            </Confirm>
            <Button width=230px on:click={printResults}>{$_('calculator.button_print')}</Button>
    </div>
</div>
